import FilePicker from "../../controls/filePicker/FilePicker";
import { useEffect, useCallback, useState, useMemo } from "react";
import PubSub from "../../../services/PubSub";
import CodeMirrorEditor from "../../controls/codeMirror/CodeMirrorEditor";
import Toolbar from "../../controls/Toolbar";
import { useTranslation } from "react-i18next";
import ConfirmationPrompt from "../../controls/ConfirmationPrompt";
import HelpModal from "../../controls/codeMirror/HelpModal";

const theme = window.localStorage.getItem('editorTheme') || "ttcn";

const SplitScreenEditor = ({
    heading,
    mode,
    configKey,
    basePath,
    emptyFile,
    validator,
    baseDataFilePath,
    initialBuildTimeConfig,
    buildTimeConfig,
    templateConfig,
    onSave,
    onLivePreviewUpdate,
    canSendUpdate,
    allowFolderCreation = true
}) => {
    const [windowWidth, setWindowWidth] = useState(0);
    let resizeWindow = () => {
        setWindowWidth(window.innerWidth);
    };
    const [translate] = useTranslation();

    const [editorData, setEditorData] = useState("");
    const [nextData, setNextData] = useState("");
    const [shouldSave, setShouldSave] = useState(false);
    const [baseData, setBaseData] = useState("");

    const [selectedFile, setSelectedFile] = useState("");
    const [overridesEditorOpenState, setOverridesEditorOpenState] = useState(1);
    const [templateEditorOpenState, setTemplateEditorOpenState] = useState(1);

    const [showHelp, setShowHelp] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const getEditorWidth = (state) => {
        switch (state) {
            case 0: return "15%";
            case 1: return "50%";
            case 2: return "85%";
            default: return "50%"
        }
    }

    const handleOnChange = useCallback((editor, data, value) => {
        setEditorData(value);
    }, []);

    const handleOnBlur = useCallback((data) => {
        setNextData(data.valueOf().getValue());
        setShouldSave(true);
    }, []);

    const handleAddFile = useCallback((fileName) => {
        buildTimeConfig[configKey][fileName] = emptyFile;
        onSave(buildTimeConfig);
    }, [configKey, emptyFile, buildTimeConfig, onSave]);

    const handleConfirm = useCallback(() => {
        const newEditorData = initialBuildTimeConfig[configKey][selectedFile];

        setEditorData(newEditorData);
        buildTimeConfig[configKey][selectedFile] = newEditorData;
        onSave(buildTimeConfig);

        setShowConfirmation(false);
    }, [configKey, initialBuildTimeConfig, buildTimeConfig, onSave, selectedFile]);

    const handleCancel = useCallback(() => {
        setShowConfirmation(false);
    }, []);

    const handleOnSelectFile = useCallback((selectedFile) => {
        setSelectedFile(selectedFile);
        if (selectedFile) {
            setEditorData(buildTimeConfig[configKey][selectedFile]);
            setBaseData(templateConfig[configKey][baseDataFilePath ? baseDataFilePath : selectedFile]);
        }

        if (selectedFile === "") {
            setEditorData("");
            setBaseData("");
        }
    }, [configKey, baseDataFilePath, templateConfig, buildTimeConfig]);

    const handleDeleteFile = useCallback((fileName) => {
        delete buildTimeConfig[configKey][fileName];
        handleOnSelectFile("");
        onSave(buildTimeConfig);
    }, [configKey, buildTimeConfig, onSave, handleOnSelectFile]);

    const handleRestore = useCallback(() => {
        if (initialBuildTimeConfig[configKey][selectedFile] !== buildTimeConfig[configKey][selectedFile]) {
            setShowConfirmation(true);
        }
        else {
            PubSub.publish("show_info", translate("OTM_NOTHING_TO_RESTORE"), translate("OTM_INFO"));
        }
    }, [buildTimeConfig, initialBuildTimeConfig, configKey, selectedFile, translate]);

    useEffect(() => {
        if (nextData && shouldSave && selectedFile) {
            if (typeof validator === "function" && validator(nextData) === false) {
                PubSub.publish("show_info", translate("OTM_FILE_SAVE_FAILED"), translate("OTM_FILE_SAVE_FAILED_TITLE"));
            }
            else if (buildTimeConfig[configKey][selectedFile] !== nextData) {

                buildTimeConfig[configKey][selectedFile] = nextData;
                onSave(buildTimeConfig);
            }

            setShouldSave(false);
            setNextData("");
        }
    }, [nextData, selectedFile, shouldSave, configKey, buildTimeConfig, validator, onSave, translate]);

    useEffect(() => {
        resizeWindow();
        window.addEventListener("resize", resizeWindow);
        return () => window.removeEventListener("resize", resizeWindow);
    }, []);

    const renderTree = useMemo(() => {
        if (buildTimeConfig) {
            return <div className="sticky-lg-top">
                <label className="h6 text-secondary"> {translate("OTM_SELECT_FILE")} </label>
                <FilePicker
                    files={Object.keys(buildTimeConfig[configKey])}
                    basePath={basePath}
                    configKey={configKey}
                    onSelectFile={handleOnSelectFile}
                    onAddFile={handleAddFile}
                    onDeleteFile={handleDeleteFile}
                    showIcons={windowWidth > 1400}
                    allowFolderCreation={allowFolderCreation}
                />
            </div>
        }
    }, [configKey, basePath, buildTimeConfig, allowFolderCreation, windowWidth, translate, handleAddFile, handleDeleteFile, handleOnSelectFile]);

    const renderOverridesEditor = useMemo(() => {
        return <div className="d-inline-block h-100" style={{ width: `${getEditorWidth(overridesEditorOpenState)}` }}>
            <div className="ps-2">
                <Toolbar
                    buttons={[
                        {
                            name: "SettingsBackupRestore",
                            title: translate("OTM_RESTORE"),
                            onClick: handleRestore
                        },
                        {
                            name: "Remove",
                            title: translate("OTM_MINIMIZE"),
                            disabled: overridesEditorOpenState === 0,
                            onClick: () => {
                                setOverridesEditorOpenState((data) => { if (data !== 0) return data - 1; })
                                setTemplateEditorOpenState((data) => { if (data !== 2) return data + 1; })
                            }
                        },
                        {
                            name: "Fullscreen",
                            title: translate("OTM_MAXIMIZE"),
                            disabled: overridesEditorOpenState === 2,
                            onClick: () => {
                                setOverridesEditorOpenState((data) => { if (data !== 2) return data + 1; })
                                setTemplateEditorOpenState((data) => { if (data !== 0) return data - 1; })
                            }
                        },
                    ]}
                    heading={translate("OTM_OVERRIDES")}
                />
            </div>
            <CodeMirrorEditor
                heading={translate("OTM_OVERRIDES")}
                editorData={editorData}
                theme={theme}
                mode={mode}
                readOnly={selectedFile !== "" ? false : true}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
            />
        </div>
    }, [mode, editorData, selectedFile, overridesEditorOpenState, handleRestore, translate, handleOnChange, handleOnBlur]);

    const renderTemplateEditor = useMemo(() => {
        return <div className="d-inline-block h-100" style={{ width: `${getEditorWidth(templateEditorOpenState)}` }}>
            <div className="ps-2">
                <Toolbar
                    buttons={[
                        {
                            name: "Remove",
                            title: translate("OTM_MINIMIZE"),
                            disabled: templateEditorOpenState === 0,
                            onClick: () => {
                                setOverridesEditorOpenState((data) => { if (data !== 2) return data + 1; })
                                setTemplateEditorOpenState((data) => { if (data !== 0) return data - 1; })
                            }
                        },
                        {
                            name: "Fullscreen",
                            title: translate("OTM_MAXIMIZE"),
                            disabled: templateEditorOpenState === 2,
                            onClick: () => {
                                setOverridesEditorOpenState((data) => { if (data !== 0) return data - 1; })
                                setTemplateEditorOpenState((data) => { if (data !== 2) return data + 1; })
                            }
                        },
                    ]}
                    heading={translate("OTM_TEMPLATE")}
                />
            </div>
            <CodeMirrorEditor
                heading={translate("OTM_TEMPLATE")}
                editorData={baseData}
                theme={theme}
                mode={mode}
                readOnly={true}
            />
        </div>
    }, [mode, baseData, templateEditorOpenState, translate]);

    if (!buildTimeConfig || !templateConfig) {
        return <div className="col-2 pt-2 ps-2 h6">{translate("OTM_NO_CONFIG_DATA")}</div>
    }

    return <>
        <div className="row h-100">
            <HelpModal show={showHelp} onCancel={() => setShowHelp(false)} />
            <ConfirmationPrompt show={showConfirmation} message={translate("OTM_REVERT_CHANGES_IN_FILE")}
                onConfirm={handleConfirm}
                onCancel={handleCancel} />
            <div className="col-4 col-lg-2">
                {renderTree}
            </div>
            <div className="col-8 col-lg-10">
                <div className="ps-2">
                    <Toolbar
                        buttons={[
                            {
                                name: "HelpOutline",
                                title: translate("OTM_HELP"),
                                onClick: () => setShowHelp(true)
                            },
                            {
                                name: "Send",
                                title: translate("OTM_SEND_UPDATE"),
                                disabled: !canSendUpdate,
                                onClick: onLivePreviewUpdate
                            },
                        ]}
                        heading={heading}
                    />
                </div>
                <div style={{ height: 'calc(100vh - 200px)' }}>
                    {renderOverridesEditor}
                    {renderTemplateEditor}
                </div>
            </div>
        </div>
    </>
}

export default SplitScreenEditor;